import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A modal dialog: backdrop, focus trap, Escape, and focus returned to
 * whatever opened it.
 *
 * Extracted at M1-04 from `MoveJobDialog` (M1-11), which was the only modal in
 * the app until the customer page's quick actions needed two more. Three
 * hand-rolled focus traps is three chances for one of them to quietly stop
 * trapping — web/CONVENTIONS.md's point about untested modals — so there is
 * one, and its tests are `dialog.test.tsx`.
 *
 * <b>A `<div role="dialog">`, not the native `<dialog>` element.</b> jsdom
 * still has no `showModal` (checked against jsdom 30), so a native dialog
 * could not be tested at all. Escape, the trap, and the focus restore are
 * therefore explicit here, and each has a test.
 */
export function Dialog({
  titleId,
  title,
  description,
  onClose,
  children,
  footer,
  testId,
  dismissLabel,
}: {
  /** Referenced by `aria-labelledby`; must match the id on the rendered title. */
  titleId: string;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  testId?: string;
  /**
   * The backdrop's accessible name. Defaults to a plain "Close", but a dialog
   * that abandons something in progress should say so — the move dialog's
   * "Close without moving" tells a screen-reader user what dismissing costs.
   */
  dismissLabel?: string;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus the first control on open, and hand focus back to whatever opened
  // the dialog on close — otherwise a keyboard user is returned to the top of
  // the document and has to walk the whole page again.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Skips the backdrop button, which is first in the DOM but is a dismissal,
    // not where anyone wants to land.
    dialogRef.current?.querySelector<HTMLElement>('select, input, textarea, button')?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // Stopped here so an Escape meant for this dialog does not also reach
        // a listener on whatever is behind it.
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      // The trap. Without it, Tab walks out of the dialog and into the page
      // behind it, which is still rendered and still full of controls.
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, select, input, textarea, [href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) {
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* A real button, so a pointer user can dismiss by clicking away and a
          screen reader is told what the backdrop does rather than meeting an
          unlabelled div that happens to be clickable.

          `tabIndex={-1}` because it sits OUTSIDE the trapped region (the trap
          searches `dialogRef`, and this is its sibling), so it could never be
          tabbed to anyway — this states that rather than leaving it as an
          accident of where the ref happens to be. The keyboard routes out are
          Escape and the footer's Cancel. */}
      <button
        type="button"
        tabIndex={-1}
        aria-label={dismissLabel ?? t('common.dismiss')}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-lg border bg-[var(--color-background)] p-4 shadow-lg"
        data-testid={testId}
      >
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        {description ? (
          <p className="mt-0.5 mb-3 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        ) : null}

        <div className="space-y-3">{children}</div>

        <div className="mt-4 flex justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}
